import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { losApi } from '../../losApi';
import { fetchBlob, openWithAuth, downloadWithAuth, isTabSafeType } from '../../api';
import { toast } from '../../../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { Loader2, Plus, Folder, FileText, Trash2, Pencil, ShieldAlert } from 'lucide-react';
import { ModuleHeader } from '../shared/formFields';
import { DocDropZone } from '../../income-calculator/DocDropZone';
import { DOC_ACCEPT, DOC_EXTENSIONS, DEFAULT_MAX_MB, partitionFiles, describeAllowed } from '../../shared/uploads';
import { formatDate } from '../../kit/time';
import { errorMessage } from '../../errors';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';

const FOLDER_LABEL = {
  misc: 'Misc', borrower_info: 'Borrower Info', asset: 'Assets', credit: 'Credit',
  disclosures: 'Disclosures', income_emp: 'Income / Employment', property: 'Property',
};

const NO_REQUEST = '__none';
const FOLDER_OPTIONS = Object.entries(FOLDER_LABEL);

// An <img src> can't send the Bearer header (ticket 185), so fetch the image as
// a blob and show that. Falls back to the file icon while loading or on failure.
function DocThumb({ docId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null;
    let cancelled = false;
    fetchBlob(losApi.documentPath(docId)).then((blob) => {
      if (cancelled) return;
      url = URL.createObjectURL(blob);
      setSrc(url);
    }).catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [docId]);
  return src
    ? <img src={src} alt="preview" className="w-10 h-12 object-cover" />
    : <FileText size={14} className="text-slate-400" />;
}

export function DocumentsModule({ loan, onChange }) {
  const [docs, setDocs] = useState([]);
  const [folder, setFolder] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [uploadFolder, setUploadFolder] = useState('misc');
  // Client Needs rows still waiting on a file; "" = not for any request.
  const [openRequests, setOpenRequests] = useState([]);
  const [forRequest, setForRequest] = useState('');
  const [dragging, setDragging] = useState(false);
  const [viewingId, setViewingId] = useState(null);
  const [renaming, setRenaming] = useState(null); // the document open in the rename dialog
  const dragDepth = useRef(0);
  // Ticket 80: the server's live rule (cap + types); constants are the fallback.
  const [policy, setPolicy] = useState({ max_mb: DEFAULT_MAX_MB, allowed_extensions: DOC_EXTENSIONS });
  const fileInputRef = useRef(null);

  const load = () => losApi.listDocuments(loan.loan_file_id).then(setDocs);
  useEffect(() => { load(); }, [loan.loan_file_id]);
  const loadRequests = () => Promise.resolve(losApi.listDocRequests?.(loan.loan_file_id))
    .then((rows) => setOpenRequests((rows || []).filter((r) => r.status === 'requested' || r.status === 'rejected')))
    .catch(() => {});
  useEffect(() => { loadRequests(); }, [loan.loan_file_id]);
  useEffect(() => {
    losApi.uploadPolicy?.().then((p) => { if (p?.max_mb) setPolicy(p); }).catch(() => {});
  }, []);

  const allowedExts = policy.allowed_extensions?.length ? policy.allowed_extensions : DOC_EXTENSIONS;
  const accept = allowedExts.map((e) => `.${e}`).join(',') || DOC_ACCEPT;
  const allowedText = describeAllowed(allowedExts, policy.max_mb);

  // One path for the picker and for drops: pre-check type + size the way the
  // server will, say which files were skipped, upload the rest.
  const uploadFiles = async (fileList) => {
    const { ok, rejected } = partitionFiles(fileList, { extensions: allowedExts, maxMb: policy.max_mb });
    if (rejected.length) {
      toast({ title: `Skipped ${rejected.length} file${rejected.length !== 1 ? 's' : ''}`,
        description: rejected.map((r) => `${r.file.name}: ${r.reason}`).join('; ').slice(0, 300) });
    }
    if (ok.length === 0) return;
    setUploading(true);
    try {
      const res = await losApi.uploadDocuments(loan.loan_file_id, ok, { folder: uploadFolder, doc_request_id: forRequest || undefined });
      toast({ title: `Uploaded ${res.count} file${res.count !== 1 ? 's' : ''}` });
      setForRequest('');
      await load();
      loadRequests();
      onChange && onChange();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast({ title: 'Upload failed', description: (typeof d === 'string' ? d : JSON.stringify(d || String(err))).substring(0, 200) });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const handleFilesSelected = (e) => uploadFiles(e.target.files);

  // Whole module is the drop target; a counter survives child enter/leave churn.
  const onDragEnter = (e) => { e.preventDefault(); dragDepth.current += 1; setDragging(true); };
  const onDragOver = (e) => { e.preventDefault(); if (!dragging) setDragging(true); };
  const onDragLeave = (e) => { e.preventDefault(); dragDepth.current = Math.max(0, dragDepth.current - 1); if (dragDepth.current === 0) setDragging(false); };
  const onDrop = (e) => { e.preventDefault(); dragDepth.current = 0; setDragging(false); uploadFiles(e.dataTransfer?.files); };

  const viewDoc = async (d) => {
    setViewingId(d.doc_id);
    const path = losApi.documentPath(d.doc_id);
    try {
      // Word, Excel and CSV files only ever download, so skip the blank tab.
      if (isTabSafeType(d.content_type)) await openWithAuth(path, { fallbackName: d.original_filename });
      else await downloadWithAuth(path, d.original_filename);
    }
    finally { setViewingId(null); }
  };

  const doDelete = async (docId) => {
    if (!(await confirmDialog({ title: 'Remove this document?', description: 'This is a soft delete.', confirmText: 'Remove', destructive: true }))) return;
    try { await losApi.deleteDocument(docId); await load(); }
    catch { toast({ title: 'Remove failed' }); }
  };

  const moveDoc = async (d, to) => {
    if (to === (d.folder || 'misc')) return;
    try {
      await losApi.updateDocument(d.doc_id, { folder: to });
      toast({ title: `Moved to ${FOLDER_LABEL[to] || to}` });
      await load();
    } catch (e) { toast({ title: 'Could not move the document', description: errorMessage(e) }); }
  };

  const folders = useMemo(() => {
    const buckets = { all: docs.length, borrower_info: 0, asset: 0, credit: 0, disclosures: 0, income_emp: 0, property: 0, misc: 0 };
    docs.forEach((d) => { buckets[d.folder] = (buckets[d.folder] || 0) + 1; });
    return buckets;
  }, [docs]);

  const filtered = useMemo(() => {
    if (folder === 'all') return docs;
    return docs.filter(d => (d.folder || 'misc') === folder);
  }, [docs, folder]);

  const FOLDER_ROWS = [
    ['all', 'All Docs', folders.all],
    ['borrower_info', 'Borrower Info', folders.borrower_info || 0],
    ['asset', 'Assets', folders.asset || 0],
    ['credit', 'Credit', folders.credit || 0],
    ['disclosures', 'Disclosures', folders.disclosures || 0],
    ['income_emp', 'Income / Employment', folders.income_emp || 0],
    ['property', 'Property', folders.property || 0],
    ['misc', 'Misc', folders.misc || 0],
  ];

  return (
    <div className="space-y-4 rounded-xl transition-shadow" onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
      style={dragging ? { boxShadow: '0 0 0 3px rgba(107,168,46,0.45)' } : undefined} data-testid="documents-module" data-dragging={dragging ? 'true' : 'false'}>
      <ModuleHeader title="Documents" extraButtons={
        <>
          {openRequests.length > 0 && (
            <Select value={forRequest || NO_REQUEST} onValueChange={(v) => setForRequest(v === NO_REQUEST ? '' : v)}>
              <SelectTrigger data-testid="upload-request-select" aria-label="Which request this upload answers" className="text-[12px] font-semibold h-9 px-2 rounded-lg border-slate-200 bg-white max-w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_REQUEST}>Not for a request</SelectItem>
                {openRequests.map((r) => (
                  <SelectItem key={r.doc_request_id} value={r.doc_request_id}>{`Answers: ${r.label}`}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={uploadFolder} onValueChange={setUploadFolder}>
            <SelectTrigger data-testid="upload-folder-select" aria-label="Folder for the upload" className="text-[12px] font-semibold h-9 px-2 rounded-lg border-slate-200 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FOLDER_OPTIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFilesSelected}
            multiple
            accept={accept}
            style={{ display: 'none' }}
            data-testid="upload-file-input"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-btn"
            className="text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white flex items-center gap-1.5"
            style={{ background: '#070B5F' }}
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </>
      } />

      <DocDropZone
        dragging={dragging}
        analyzing={uploading}
        fileInputRef={fileInputRef}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        analyzeDocs={uploadFiles}
        accept={accept}
        testId="loan-docs-drop-zone"
        inputTestId="loan-docs-drop-input"
        hintTestId="loan-docs-drop-zone-hint"
        title="Drag & drop documents here"
        draggingTitle="Release to upload"
        subtitle={<>Drop anywhere on this panel. Files land in the <b>{FOLDER_LABEL[uploadFolder] || uploadFolder}</b> folder (change it above). Multiple files at once.</>}
        hint={allowedText}
        busyTitle="Uploading…"
        busySubtitle="Storing files and adding them to the loan file."
        minHeight={120}
      />

      <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-2">
          {FOLDER_ROWS.map(([k, label, count]) => (
            <button
              key={k}
              onClick={() => setFolder(k)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-[13px] text-left ${folder === k ? 'font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}
              style={folder === k ? { background: 'rgba(107,168,46,0.1)', color: '#070B5F' } : {}}
              data-testid={`docs-folder-${k}`}
            >
              <span className="flex items-center gap-2"><Folder size={13} />{label}</span>
              <span className="text-[11px] font-mono text-slate-400">{count}</span>
            </button>
          ))}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-[12px] font-semibold text-slate-500 uppercase tracking-widest flex items-center justify-between">
            {folder === 'all' ? 'All Documents' : FOLDER_ROWS.find(f => f[0] === folder)?.[1]}
            <span className="text-slate-400 normal-case tracking-normal">{filtered.length} file{filtered.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="divide-y divide-slate-100">
            {filtered.length === 0 && (
              <div className="p-10 text-center">
                <Folder size={32} className="mx-auto mb-2 text-slate-300" />
                <div className="text-[13px] font-semibold" style={{ color: '#070B5F' }}>No documents yet</div>
                <div className="text-[11px] text-slate-500 mt-1">Drop files above or click "Upload" — {allowedText}.</div>
              </div>
            )}
            {filtered.map((d) => (
              <div key={d.doc_id} className="p-3 flex items-center gap-3 hover:bg-slate-50/60 group" data-testid={`doc-row-${d.doc_id}`}>
                <div className="w-10 h-12 rounded border border-slate-200 bg-slate-50 flex items-center justify-center shrink-0 overflow-hidden">
                  {(d.content_type || '').startsWith('image/')
                    ? <DocThumb docId={d.doc_id} />
                    : <FileText size={14} className="text-slate-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[13px] truncate" style={{ color: '#070B5F' }}>{d.original_filename}</div>
                  <div className="text-[11px] text-slate-500">
                    {(d.size / 1024).toFixed(1)} KB · {FOLDER_LABEL[d.folder] || d.folder} · {formatDate(d.created_at)}
                    {d.uploaded_by_name && ` · ${d.uploaded_by_name}`}
                  </div>
                </div>
                {d.needs_masking && (
                  // Ticket 176: a borrower scan held for the SSN mask review; the editor lives on
                  // the contact's Borrower uploads card.
                  loan?.contact_id ? (
                    <Link to={`/portal/contacts/${loan.contact_id}`}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 shrink-0"
                      title="Audrey needs the SSN masked before reading this file. Review it on the contact's Borrower uploads card."
                      data-testid={`doc-ssn-review-${d.doc_id}`}>
                      <ShieldAlert size={10} /> SSN review
                    </Link>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 shrink-0"
                      data-testid={`doc-ssn-review-${d.doc_id}`}><ShieldAlert size={10} /> SSN review</span>
                  )
                )}
                <button
                  onClick={() => viewDoc(d)}
                  disabled={viewingId === d.doc_id}
                  className="text-[11px] font-semibold px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                  data-testid={`doc-view-${d.doc_id}`}
                >{viewingId === d.doc_id ? 'Opening…' : 'View'}</button>
                <Select value={FOLDER_LABEL[d.folder] ? d.folder : 'misc'} onValueChange={(to) => moveDoc(d, to)}>
                  <SelectTrigger className="h-7 w-[124px] text-[11px] px-2 shrink-0" aria-label={`Move ${d.original_filename} to a folder`} data-testid={`doc-move-${d.doc_id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FOLDER_OPTIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button
                  onClick={() => setRenaming(d)}
                  className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition text-slate-400 hover:text-slate-700 p-1"
                  aria-label={`Rename ${d.original_filename}`}
                  data-testid={`doc-rename-${d.doc_id}`}
                ><Pencil size={13} /></button>
                <button
                  onClick={() => doDelete(d.doc_id)}
                  className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition text-slate-400 hover:text-red-500 p-1"
                  aria-label={`Remove ${d.original_filename}`}
                  data-testid={`doc-delete-${d.doc_id}`}
                ><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
      {renaming && (
        <RenameDocDialog
          doc={renaming}
          onClose={() => setRenaming(null)}
          onSaved={() => { setRenaming(null); load(); }}
        />
      )}
    </div>
  );
}

// Renames what staff see and what the download is saved as. The stored file is not touched.
function RenameDocDialog({ doc, onClose, onSaved }) {
  const [name, setName] = useState(doc.original_filename || '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const next = name.trim();
    if (!next) return;
    if (next === doc.original_filename) { onClose(); return; }
    setSaving(true);
    try {
      await losApi.updateDocument(doc.doc_id, { original_filename: next });
      onSaved();
    } catch (e) {
      toast({ title: 'Could not rename the document', description: errorMessage(e) });
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogTitle className="font-serif text-lg font-bold mb-4" style={{ color: '#030524' }}>Rename document</DialogTitle>
        <Input value={name} maxLength={255} autoFocus onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} data-testid="rename-doc-input" aria-label="File name" />
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={saving || !name.trim()} className="text-sm px-4 py-2 rounded-lg text-white disabled:opacity-50" style={{ background: '#070B5F' }} data-testid="save-rename-doc">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
